import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

export interface CartItemModifier {
  modifier_id: string;
  modifier_list_id: string;

  name: string;
  price_cents: number;
  quantity: number;
  text_value?: string;
  is_text_modifier?: boolean;
}

export interface CartItem {
  id: string;
  name: string;
  price_cents: number;
  quantity: number;
  image_url: string | null;
  modifiers?: CartItemModifier[];
  modifiers_total_cents?: number;
  cartItemKey?: string;

  metadata?: {
    type?: "gift_card" | "balance_topup";
    recipientEmail?: string;
    recipientPhone?: string;
    recipientMessage?: string;
    customerId?: string;
  };
}

export type OrderType = "pickup" | "dine_in" | "delivery" | "popup";

// Generate a unique key for a cart item based on product ID and selected modifiers
function generateCartItemKey(productId: string, modifiers?: CartItemModifier[]): string {
  if (!modifiers || modifiers.length === 0) {
    return productId;
  }
  const modifierKey = modifiers
    .slice()
    .sort((a, b) => a.modifier_id.localeCompare(b.modifier_id))
    .map(m => `${m.modifier_id}:${m.quantity}`)
    .join('|');
  return `${productId}__${modifierKey}`;
}

interface CartContextType {
  items: CartItem[];
  businessId: string | null;
  subdomain: string | null;
  selectedLocationId: string | null;
  tableId: string | null;
  orderType: OrderType;
  addItem: (item: Omit<CartItem, "quantity" | "cartItemKey" | "modifiers_total_cents"> & { quantity?: number, forceNewEntry?: boolean }) => void;
  removeItem: (cartItemKey: string) => void;
  updateQuantity: (cartItemKey: string, quantity: number) => void;
  clearCart: () => void;
  setBusinessInfo: (businessId: string, subdomain: string) => void;
  setSelectedLocation: (locationId: string | null) => void;
  setTableInfo: (tableId: string | null, locationId?: string | null) => void;
  setOrderType: (type: OrderType) => void;
  totalItems: number;
  totalCents: number;
  popupLocationId: string | null;
  popupLocationName: string | null;
  setPopupLocation: (id: string | null, name: string | null) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [subdomain, setSubdomain] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [tableId, setTableId] = useState<string | null>(null);
  const [orderType, setOrderTypeState] = useState<OrderType>("pickup");
  const [popupLocationId, setPopupLocationId] = useState<string | null>(null);
  const [popupLocationName, setPopupLocationName] = useState<string | null>(null);

  // Load cart from sessionStorage on mount (persists across refreshes, clears on tab close)
  useEffect(() => {
    // Load business context from localStorage (persists across sessions)
    const savedBusiness = localStorage.getItem("cart_business");
    if (savedBusiness) {
      try {
        const parsed = JSON.parse(savedBusiness);
        setBusinessId(parsed.businessId || null);
        setSubdomain(parsed.subdomain || null);
      } catch (e) {
        console.error("Error loading business context:", e);
      }
    }

    // Load cart state from sessionStorage (persists across refreshes within same tab)
    const savedSession = sessionStorage.getItem("cart_session");
    if (savedSession) {
      try {
        const parsed = JSON.parse(savedSession);
        setItems(parsed.items || []);
        setSelectedLocationId(parsed.selectedLocationId || null);
        setTableId(parsed.tableId || null);
        setOrderTypeState(parsed.orderType || "pickup");
        setPopupLocationId(parsed.popupLocationId || null);
        setPopupLocationName(parsed.popupLocationName || null);
      } catch (e) {
        console.error("Error loading cart session:", e);
      }
    }
  }, []);

  // Save business context to localStorage (persists across sessions)
  useEffect(() => {
    localStorage.setItem("cart_business", JSON.stringify({
      businessId, subdomain
    }));
  }, [businessId, subdomain]);

  useEffect(() => {
    sessionStorage.setItem("cart_session", JSON.stringify({
      items, selectedLocationId, tableId, orderType, popupLocationId, popupLocationName
    }));
  }, [items, selectedLocationId, tableId, orderType, popupLocationId, popupLocationName]);

  const setBusinessInfo = useCallback((newBusinessId: string, newSubdomain: string) => {
    // If switching businesses, clear the cart and location
    if (businessId && businessId !== newBusinessId) {
      setItems([]);
      setSelectedLocationId(null);
      setTableId(null);
      setOrderTypeState("pickup");
    }
    setBusinessId(newBusinessId);
    setSubdomain(newSubdomain);
  }, [businessId]);

  const setSelectedLocation = useCallback((locationId: string | null) => {
    setSelectedLocationId(locationId);
  }, []);

  const setTableInfo = useCallback((newTableId: string | null, locationId?: string | null) => {
    setTableId(newTableId);
    if (newTableId) {
      setOrderTypeState("dine_in");
      if (locationId) {
        setSelectedLocationId(locationId);
      }
    }
  }, []);

  const setOrderType = useCallback((type: OrderType) => {
    setOrderTypeState(type);
  }, []);

  const setPopupLocation = useCallback((id: string | null, name: string | null) => {
    setPopupLocationId(id);
    setPopupLocationName(name);
    if (id) {
      setOrderTypeState("popup");
    }
  }, []);

  const addItem = useCallback((item: Omit<CartItem, "quantity" | "cartItemKey" | "modifiers_total_cents"> & { quantity?: number, forceNewEntry?: boolean }) => {
    let cartItemKey = generateCartItemKey(item.id, item.modifiers);

    // If forceNewEntry is true, append a unique suffix to ensure it's treated as a separate line item
    if (item.forceNewEntry) {
      cartItemKey += `__copy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    const modifiersTotalCents = item.modifiers?.reduce((sum, m) => sum + (m.price_cents * m.quantity), 0) || 0;
    const quantityToAdd = item.quantity || 1;

    setItems((prev) => {
      // Only check for existing if we are NOT forcing a new entry
      // Although even if we forced it, the key is now unique so it won't be found essentially.
      // But purely relying on the key is safest.
      const existing = prev.find((i) => i.cartItemKey === cartItemKey);

      if (existing) {
        return prev.map((i) =>
          i.cartItemKey === cartItemKey
            ? { ...i, quantity: i.quantity + quantityToAdd }
            : i
        );
      }
      return [...prev, {
        ...item,
        quantity: quantityToAdd,
        cartItemKey,
        modifiers_total_cents: modifiersTotalCents
      }];
    });
  }, []);

  const removeItem = useCallback((cartItemKey: string) => {
    setItems((prev) => prev.filter((i) => i.cartItemKey !== cartItemKey));
  }, []);

  const updateQuantity = useCallback((cartItemKey: string, quantity: number) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((i) => i.cartItemKey !== cartItemKey));
    } else {
      setItems((prev) =>
        prev.map((i) => (i.cartItemKey === cartItemKey ? { ...i, quantity } : i))
      );
    }
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setSelectedLocationId(null);
    setTableId(null);
    setOrderTypeState("pickup");
    setPopupLocationId(null);
    setPopupLocationName(null);
    sessionStorage.removeItem("cart_session");
  }, []);

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalCents = items.reduce((sum, item) => {
    const itemTotal = item.price_cents + (item.modifiers_total_cents || 0);
    return sum + itemTotal * item.quantity;
  }, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        businessId,
        subdomain,
        selectedLocationId,
        tableId,
        orderType,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        setBusinessInfo,
        setSelectedLocation,
        setTableInfo,
        setOrderType,
        totalItems,
        totalCents,
        popupLocationId,
        popupLocationName,
        setPopupLocation,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
